// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_CORE_URL;
const API_ENDPOINT = '/api/User/user';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_CORE_SCOPE;

const HEADERS = {
  'Content-Type': 'application/json',
};

// ============= Types =============

export interface PostUserProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  body: string[];
}

export interface PostUserResponse {
  id: string;
  name: string;
  jobTitle: string;
  username: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  department: string;
  companyName: string;
  country: string;
  office: string;
  proNoun: string;
  title: string;
  isOwner: boolean;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Creates or updates a user
 * @param getAccessToken Function to acquire access token
 * @param body Optional request body
 * @returns Promise with API response or error
 */
export const postUser = async ({
  getAccessToken,
  body,
}: PostUserProps): Promise<PostUserResponse[]> => {
  try {
    console.log('🔄 Posting user...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'POST',
      headers: createHeaders(token),
      body: JSON.stringify(body),
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ User posted successfully: ${data}`);

    return data;
  } catch (error) {
    console.error('🔴 Error posting user:', error);
    throw error;
  }
};

